import { apiAxios } from '@/api/axios';
import type { RevisionEntry } from '@/api/types/Audit';
import type {
  CalendarFilters,
  CursorPage,
  ExportFormat,
  JizdaCreate,
  JizdaDispatcherFieldPatch,
  JizdaDispatcherPatch,
  JizdaListItem,
  JizdaNotePatch,
  JizdaResponse,
  JizdaStornoRequest,
  JizdaUpdate,
  JizdyFilters,
} from '@/api/types/Jizda';
import { getWeekDays, getWeekStart } from '@/utils/date';

// Jízdy service — wraps `/api/jizdy/*` (CHR-02 / ADR-0036). Functional, one
// export per endpoint. Cursor pagination per ADR-0004: `cursor` is opaque,
// just pass the value the api returned in `nextCursor`.
//
// RBAC by endpoint-split (server-enforced): a dispatcher's only writes are
// `dispatcher-fields` (carrier/carrierType/vehicle/driverName) and `dispatcher`
// (self-assign/unassign). `PUT`, both note PATCHes, `POST`, and `DELETE` are
// backoffice-only; the api returns 403 for a dispatcher.

export const JIZDY_PAGE_LIMIT = 50;

const buildListParams = (
  filters: JizdyFilters,
  cursor: string | null,
): Record<string, string> => {
  const params: Record<string, string> = {
    showDeleted: String(filters.showDeleted),
    limit: String(JIZDY_PAGE_LIMIT),
  };
  // Only serialize `from` when it is a non-empty ISO date. An empty string
  // cannot bind to the api's `LocalDate from` → HTTP 400. Omitting `from`
  // entirely is the safe "no value" signal; the api defaults to today.
  if (filters.from) params.from = filters.from;
  if (filters.dispatcher) params.dispatcher = filters.dispatcher;
  if (filters.assigned !== undefined) params.assigned = String(filters.assigned);
  // Tri-state storno filter (CHR-02 / ADR-0039). Canonical wire name = `storno`
  // (true → only STORNO, false → exclude); omit when undefined ("all"), exactly
  // like the `assigned` tri-state above.
  if (filters.storno !== undefined) params.storno = String(filters.storno);
  // Commodity overview filter (CHR-04 / ADR-0043) — pass the UUID through to
  // the server-side `commodityId` predicate; omit when unset.
  if (filters.commodityId) params.commodityId = filters.commodityId;
  // Omit sort params when they equal the api defaults (`createdAt asc`) to
  // keep request URLs clean; api defaults match exactly.
  if (filters.sortBy && filters.sortBy !== 'createdAt') params.sortBy = filters.sortBy;
  if (filters.sortDir && filters.sortDir !== 'asc') params.sortDir = filters.sortDir;
  if (cursor) params.cursor = cursor;
  return params;
};

export const listJizdy = async (
  filters: JizdyFilters,
  cursor: string | null = null,
): Promise<CursorPage<JizdaListItem>> => {
  const response = await apiAxios.get<CursorPage<JizdaListItem>>('/api/jizdy', {
    params: buildListParams(filters, cursor),
  });
  return response.data;
};

// Bounded week-fetch for the weekly transport calendar (CHR-03 §6 / ADR-0051).
// `GET /api/jizdy?from={Mon}&to={Sun}` — when `to` is present the api returns
// ALL `JizdaListItem` rows with `from ≤ loadingDate ≤ to` (null-loadingDate
// rows excluded), `nextCursor: null`, no 50-cap. Same envelope as the list
// endpoint, so we read `items`. `weekStartIso` is normalised to its Monday
// (defensive — callers already pass a Monday) and the upper bound is its
// Sunday via `getWeekDays`. Calendar filters map onto the existing query
// params; `showStorno=false` becomes `storno=false` (exclude), `true` omits
// `storno` (default — storno rows show inline).
export const listJizdyWeek = async (
  weekStartIso: string,
  filters: CalendarFilters,
): Promise<JizdaListItem[]> => {
  const monday = getWeekStart(weekStartIso);
  const week = getWeekDays(monday);
  // `getWeekDays` returns 7 entries; the Sunday is index 6. Fall back to the
  // Monday for the impossible empty-array case so `to` always binds.
  const sunday = week[6] ?? monday;
  const params: Record<string, string> = {
    from: monday,
    to: sunday,
    showDeleted: String(filters.showDeleted),
  };
  if (filters.dispatcher) params.dispatcher = filters.dispatcher;
  if (filters.commodityId) params.commodityId = filters.commodityId;
  if (filters.assigned !== undefined) params.assigned = String(filters.assigned);
  // showStorno=false → exclude storno; true → omit (api default shows storno
  // inline). Mirrors the overview's tri-state `storno` param semantics.
  if (!filters.showStorno) params.storno = 'false';
  const response = await apiAxios.get<CursorPage<JizdaListItem>>('/api/jizdy', {
    params,
  });
  return response.data.items;
};

export const getJizda = async (id: string): Promise<JizdaResponse> => {
  const response = await apiAxios.get<JizdaResponse>(`/api/jizdy/${id}`);
  return response.data;
};

export const createJizda = async (payload: JizdaCreate): Promise<JizdaResponse> => {
  const response = await apiAxios.post<JizdaResponse>('/api/jizdy', payload);
  return response.data;
};

export const updateJizda = async (
  id: string,
  payload: JizdaUpdate,
): Promise<JizdaResponse> => {
  const response = await apiAxios.put<JizdaResponse>(`/api/jizdy/${id}`, payload);
  return response.data;
};

// Dispatcher-only field-scope write (carrier/carrierType/vehicle/driverName).
export const patchJizdaDispatcherFields = async (
  id: string,
  payload: JizdaDispatcherFieldPatch,
): Promise<JizdaResponse> => {
  const response = await apiAxios.patch<JizdaResponse>(
    `/api/jizdy/${id}/dispatcher-fields`,
    payload,
  );
  return response.data;
};

// Self-assign / unassign (dispatcher or backoffice).
export const patchJizdaDispatcher = async (
  id: string,
  payload: JizdaDispatcherPatch,
): Promise<JizdaResponse> => {
  const response = await apiAxios.patch<JizdaResponse>(
    `/api/jizdy/${id}/dispatcher`,
    payload,
  );
  return response.data;
};

// Note for dispatcher (overview note) — backoffice-only.
export const patchJizdaNoteForDispatcher = async (
  id: string,
  payload: JizdaNotePatch,
): Promise<JizdaResponse> => {
  const response = await apiAxios.patch<JizdaResponse>(
    `/api/jizdy/${id}/note/dispatcher`,
    payload,
  );
  return response.data;
};

// Note for customer (internal/commercial, detail-only) — backoffice-only.
export const patchJizdaNoteForCustomer = async (
  id: string,
  payload: JizdaNotePatch,
): Promise<JizdaResponse> => {
  const response = await apiAxios.patch<JizdaResponse>(
    `/api/jizdy/${id}/note/customer`,
    payload,
  );
  return response.data;
};

export const deleteJizda = async (
  id: string,
  version: number,
  reason: string,
): Promise<void> => {
  await apiAxios.delete(`/api/jizdy/${id}`, { params: { version, reason } });
};

// Storno / un-storno state-setter (CHR-02 / ADR-0039) — backoffice-only.
// `stornoState: 'STORNO'` cancels the jízda (red row + notify all dispatchers);
// `'ACTIVE'` un-storno restores it. `noteForCustomer` carries the (optional)
// reason. Response = full `JizdaResponse`.
export const stornoJizda = async (
  id: string,
  payload: JizdaStornoRequest,
): Promise<JizdaResponse> => {
  const response = await apiAxios.post<JizdaResponse>(
    `/api/jizdy/${id}/storno`,
    payload,
  );
  return response.data;
};

// Export result — the file blob plus the server-supplied download filename
// (parsed from `Content-Disposition`, `null` when the header is absent so the
// caller can derive a fallback name).
export interface JizdaExportResult {
  blob: Blob;
  filename: string | null;
}

// Parse `attachment; filename="jizdy-export_2026-05-01_2026-05-31.csv"` →
// the bare filename. Tolerates optional quotes and surrounding whitespace;
// ignores RFC-5987 `filename*` (the api emits a plain ASCII `filename`).
const parseContentDispositionFilename = (header: string | null): string | null => {
  if (!header) return null;
  const match = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  const captured = match?.[1];
  return captured ? captured.trim() : null;
};

// Jízdy export (CHR-02 / ADR-0041, zadání §7) — backoffice-only. Closed-period
// financial extract for a `from`/`to` date range in `csv` or `xlsx`. MUST go
// through the axios instance (NOT a plain `<a href>`) so the bearer-token
// request interceptor runs — a raw download would 401. `responseType:'blob'`
// keeps the binary payload intact. Returns the blob + parsed download filename.
export const exportJizdy = async (
  from: string,
  to: string,
  format: ExportFormat,
): Promise<JizdaExportResult> => {
  const response = await apiAxios.get('/api/jizdy/export', {
    params: { from, to, format },
    responseType: 'blob',
  });
  const filename = parseContentDispositionFilename(
    // Axios lowercases response header names.
    (response.headers['content-disposition'] as string | undefined) ?? null,
  );
  return { blob: response.data as Blob, filename };
};

// Audit history per AUDIT-* / UI-04. Server returns revisions newest-first;
// the FE preserves that order (no re-sort). History is now flat scalar diffs
// (no loadings sub-table — ADR-0036).
export const getJizdaHistory = async (id: string): Promise<RevisionEntry[]> => {
  const response = await apiAxios.get<RevisionEntry[]>(`/api/jizdy/${id}/history`);
  return response.data;
};
